/**
 * Admin: Manage Duplicate Events
 *
 * Wraps the `/api/eventInfoDuplicates` endpoint:
 *   GET    ?action=count               (aggregate counts)
 *   GET    ?action=list                (paginated records)
 *   DELETE ?action=duplicates&confirm  (collapse groups, keep oldest)
 *   DELETE ?action=cleanup&confirm     (drop >1y past / >6mo future)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Trash2, RefreshCw, ChevronLeft, ChevronRight, CalendarX } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  getDuplicateCount,
  listDuplicates,
  deleteDuplicates,
  cleanupStaleEvents,
  saveLastRun,
  loadLastRun,
  type DuplicateCountResponse,
  type DuplicateListResponse,
  type DuplicateDeleteResponse,
  type DuplicateRecord,
  type LastRunRecord,
} from '../services/adminApi';
import {
  Card,
  CardTitle,
  CardDescription,
  Banner,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  RunningIndicator,
  ConfirmDialog,
  StatGrid,
  Metric,
  RawJson,
  LastRunCard,
  SampleTable,
  ButtonRow,
  Field,
  InlineLabel,
  Select,
} from '../components/admin/AdminUI';
import { formatEventLocalDateTime } from '../utils/eventDateTime';

const ACTION_DUPES = 'eventInfoDuplicates.duplicates';
const ACTION_CLEANUP = 'eventInfoDuplicates.cleanup';

type DialogKind = null | 'duplicates' | 'cleanup';

const formatDate = (s?: string): string => {
  return formatEventLocalDateTime(s) || s || '—';
};

export const AdminDuplicatesPage: React.FC = () => {
  const { userInfo } = useAuth();

  const [counts, setCounts] = useState<DuplicateCountResponse | null>(null);
  const [countLoadedAt, setCountLoadedAt] = useState<Date | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [list, setList] = useState<DuplicateListResponse | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [opResult, setOpResult] = useState<DuplicateDeleteResponse | null>(null);
  const [opKind, setOpKind] = useState<'duplicates' | 'cleanup' | null>(null);

  const [dialog, setDialog] = useState<DialogKind>(null);

  const [lastRunDupes, setLastRunDupes] =
    useState<LastRunRecord<DuplicateDeleteResponse> | null>(null);
  const [lastRunCleanup, setLastRunCleanup] =
    useState<LastRunRecord<DuplicateDeleteResponse> | null>(null);

  useEffect(() => {
    setLastRunDupes(loadLastRun(ACTION_DUPES));
    setLastRunCleanup(loadLastRun(ACTION_CLEANUP));
  }, []);

  const refreshCount = useCallback(async () => {
    setCountLoading(true);
    setCountError(null);
    try {
      const data = await getDuplicateCount();
      setCounts(data);
      setCountLoadedAt(new Date());
    } catch (e: any) {
      setCountError(e?.message ?? 'Failed to load duplicate counts');
    } finally {
      setCountLoading(false);
    }
  }, []);

  const fetchList = useCallback(async (p: number, l: number) => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await listDuplicates(p, l);
      setList(data);
    } catch (e: any) {
      setListError(e?.message ?? 'Failed to load duplicate list');
    } finally {
      setListLoading(false);
    }
  }, []);

  // initial load
  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  // refetch list whenever page/limit changes (only after first load)
  useEffect(() => {
    if (list !== null) {
      fetchList(page, limit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const doDeleteDuplicates = async () => {
    setDialog(null);
    setRunning(true);
    setOpKind('duplicates');
    setOpError(null);
    setOpResult(null);
    setStartedAt(Date.now());
    const t0 = performance.now();
    try {
      const data = await deleteDuplicates();
      setOpResult(data);
      const record: LastRunRecord<DuplicateDeleteResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        durationMs: Math.round(performance.now() - t0),
        summary: {
          deletedEventInfo: data.deletedEventInfoRecords ?? 0,
          deletedEventInfoMap: data.deletedEventInfoMapRecords ?? 0,
          groupsProcessed: data.duplicateGroupsProcessed ?? 0,
          preserved: data.preservedRecords ?? 0,
          searchDocsDeleted: data.searchIndexCleanup?.deletedSearchDocuments ?? 0,
        },
        response: data,
      };
      saveLastRun(ACTION_DUPES, record);
      setLastRunDupes(record);
      // refresh counts/list to reflect the change
      await refreshCount();
      if (list !== null) await fetchList(1, limit);
      setPage(1);
    } catch (e: any) {
      setOpError(e?.message ?? 'Delete failed');
    } finally {
      setRunning(false);
      setStartedAt(null);
    }
  };

  const doCleanupStale = async () => {
    setDialog(null);
    setRunning(true);
    setOpKind('cleanup');
    setOpError(null);
    setOpResult(null);
    setStartedAt(Date.now());
    const t0 = performance.now();
    try {
      const data = await cleanupStaleEvents();
      setOpResult(data);
      const record: LastRunRecord<DuplicateDeleteResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        durationMs: Math.round(performance.now() - t0),
        summary: {
          deletedEventInfo: data.deletedEventInfoRecords ?? 0,
          deletedEventInfoMap: data.deletedEventInfoMapRecords ?? 0,
          searchDocsDeleted: data.searchIndexCleanup?.deletedSearchDocuments ?? 0,
        },
        response: data,
      };
      saveLastRun(ACTION_CLEANUP, record);
      setLastRunCleanup(record);
      await refreshCount();
      if (list !== null) await fetchList(1, limit);
      setPage(1);
    } catch (e: any) {
      setOpError(e?.message ?? 'Cleanup failed');
    } finally {
      setRunning(false);
      setStartedAt(null);
    }
  };

  const groupCount = counts?.duplicateGroups ?? 0;
  const totalDupes = counts?.totalDuplicateRecords ?? 0;
  const recordsToDelete = Math.max(0, totalDupes - groupCount);

  // Group the visible page by (title|start|end|type|state|isVirtual) so the UI
  // can show duplicate groups together. Records are already ordered by group
  // and `duplicateRank` ASC by the backend.
  const groupKey = (r: DuplicateRecord): string =>
    [r.eventTitle, r.startDate, r.endDate, r.type, r.state, r.isVirtual]
      .map((v) => (v == null ? '' : String(v)))
      .join('|');

  const groupedRows: Array<{ key: string; records: DuplicateRecord[] }> = [];
  if (list?.records) {
    let current: { key: string; records: DuplicateRecord[] } | null = null;
    for (const r of list.records) {
      const k = groupKey(r);
      if (!current || current.key !== k) {
        current = { key: k, records: [] };
        groupedRows.push(current);
      }
      current.records.push(r);
    }
  }

  return (
    <AdminLayout
      currentPage="duplicates"
      pageTitle="Manage Duplicate Events"
      pageDescription="Find and remove duplicate EventInfo records and drop events that are too far in the past or future to be useful."
    >
      {/* ---------------------------- Counts card ---------------------------- */}
      <Card>
        <CardTitle>Duplicate counts</CardTitle>
        <CardDescription>
          Two records are considered duplicates when title, start date, end
          date, type, state, and virtual flag all match. Venue is intentionally
          ignored so the same event listed by multiple sources still
          de-duplicates.
        </CardDescription>

        <StatGrid>
          <Metric
            label="Duplicate groups"
            value={countLoading ? '…' : groupCount.toLocaleString()}
            tone={groupCount > 0 ? 'warning' : 'success'}
          />
          <Metric
            label="Records in duplicate groups"
            value={countLoading ? '…' : totalDupes.toLocaleString()}
          />
          <Metric
            label="Would delete"
            hint="Total minus one preserved per group"
            value={countLoading ? '…' : recordsToDelete.toLocaleString()}
            tone={recordsToDelete > 0 ? 'warning' : 'success'}
          />
          <Metric
            label="Avg per group"
            value={
              countLoading
                ? '…'
                : (counts?.summary?.averageDuplicatesPerGroup ?? 0).toFixed(2)
            }
          />
          <Metric
            label="Max in single group"
            value={
              countLoading
                ? '…'
                : (counts?.summary?.maxDuplicatesInSingleGroup ?? 0).toLocaleString()
            }
          />
        </StatGrid>

        {countError && <Banner tone="error">{countError}</Banner>}

        <ButtonRow>
          <PrimaryButton onClick={refreshCount} disabled={countLoading || running}>
            <RefreshCw size={16} />
            {countLoading ? 'Refreshing…' : 'Refresh counts'}
          </PrimaryButton>
          {countLoadedAt && !countLoading && (
            <span style={{ fontSize: 13, color: '#595959' }}>
              Loaded {countLoadedAt.toLocaleTimeString()}
            </span>
          )}
        </ButtonRow>
      </Card>

      {/* ---------------------------- Browse list ---------------------------- */}
      <Card>
        <CardTitle>Browse duplicate records</CardTitle>
        <CardDescription>
          Inspect the duplicate records before deleting. Within each group, the
          row marked <strong>rank&nbsp;1</strong> is the one that will be{' '}
          <strong>kept</strong> (oldest <code>Id</code>); all other rows in the
          group will be removed.
        </CardDescription>

        <ButtonRow>
          <PrimaryButton
            onClick={() => fetchList(page, limit)}
            disabled={listLoading || running}
          >
            <RefreshCw size={16} />
            {list === null ? 'Load duplicates' : 'Reload'}
          </PrimaryButton>
          <Field style={{ flex: '0 0 auto', marginBottom: 0 }}>
            <InlineLabel>
              Per page
              <Select
                value={limit}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setLimit(n);
                  setPage(1);
                }}
                disabled={listLoading || running}
                style={{ marginLeft: 8 }}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </Select>
            </InlineLabel>
          </Field>
        </ButtonRow>

        {listLoading && (
          <RunningIndicator
            startedAt={startedAt ?? Date.now()}
            message="Loading duplicate records…"
          />
        )}
        {listError && <Banner tone="error">{listError}</Banner>}

        {list && list.records.length === 0 && (
          <Banner tone="success">No duplicate records found. 🎉</Banner>
        )}

        {list && list.records.length > 0 && (
          <>
            <SampleTable>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Rank</th>
                  <th style={{ width: 90 }}>EventInfoId</th>
                  <th>Title</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Type</th>
                  <th>City / State</th>
                  <th>Virtual</th>
                  <th>Sources</th>
                </tr>
              </thead>
              <tbody>
                {groupedRows.map((g, gi) => (
                  <React.Fragment key={g.key + gi}>
                    {g.records.map((r, ri) => {
                      const keep = (r.duplicateRank ?? 0) === 1;
                      return (
                        <tr
                          key={r.eventInfoId}
                          style={{
                            background: keep
                              ? '#f6ffed'
                              : ri === 0
                                ? '#fff'
                                : '#fafafa',
                            borderTop: ri === 0 ? '2px solid #d9d9d9' : undefined,
                          }}
                        >
                          <td>
                            <strong
                              style={{
                                color: keep ? '#52c41a' : '#cf1322',
                              }}
                            >
                              {keep ? 'KEEP' : '#' + (r.duplicateRank ?? '?')}
                            </strong>
                          </td>
                          <td>{r.eventInfoId}</td>
                          <td>
                            {r.eventUrl ? (
                              <a
                                href={r.eventUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {r.eventTitle ?? '—'}
                              </a>
                            ) : (
                              r.eventTitle ?? '—'
                            )}
                          </td>
                          <td>{formatDate(r.startDate)}</td>
                          <td>{formatDate(r.endDate)}</td>
                          <td>{r.type ?? '—'}</td>
                          <td>
                            {[r.city, r.state].filter(Boolean).join(', ') || '—'}
                          </td>
                          <td>{r.isVirtual ? 'Yes' : 'No'}</td>
                          <td>{r.eventInfoMapRecords?.length ?? 0}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </SampleTable>

            <ButtonRow>
              <SecondaryButton
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!list.pagination.hasPreviousPage || listLoading || running}
              >
                <ChevronLeft size={16} />
                Prev
              </SecondaryButton>
              <span style={{ fontSize: 13, color: '#595959' }}>
                Page {list.pagination.page} of {list.pagination.totalPages} —{' '}
                {list.pagination.totalItems.toLocaleString()} records
              </span>
              <SecondaryButton
                onClick={() => setPage((p) => p + 1)}
                disabled={!list.pagination.hasNextPage || listLoading || running}
              >
                Next
                <ChevronRight size={16} />
              </SecondaryButton>
            </ButtonRow>
          </>
        )}
      </Card>

      {/* ----------------------- Delete duplicates card --------------------- */}
      <Card>
        <CardTitle>Delete duplicate records</CardTitle>
        <CardDescription>
          Collapses every duplicate group: keeps the oldest <code>Id</code> per
          group and deletes the rest. Cascades to <code>EventInfoMap</code>{' '}
          children and removes corresponding documents from the Azure AI Search
          index. This action <strong>cannot be undone</strong>.
        </CardDescription>

        {running && opKind === 'duplicates' && startedAt != null && (
          <RunningIndicator
            startedAt={startedAt}
            message="Deleting duplicate records…"
          />
        )}

        {opKind === 'duplicates' && opError && (
          <Banner tone="error">{opError}</Banner>
        )}

        {opKind === 'duplicates' && opResult && !opError && (
          <>
            <Banner tone="success">
              {opResult.summary ?? 'Duplicates removed successfully.'}
            </Banner>
            <StatGrid>
              <Metric
                label="Groups processed"
                value={(opResult.duplicateGroupsProcessed ?? 0).toLocaleString()}
              />
              <Metric
                label="Preserved"
                value={(opResult.preservedRecords ?? 0).toLocaleString()}
                tone="success"
              />
              <Metric
                label="EventInfo deleted"
                value={(opResult.deletedEventInfoRecords ?? 0).toLocaleString()}
                tone="warning"
              />
              <Metric
                label="EventInfoMap deleted"
                value={(opResult.deletedEventInfoMapRecords ?? 0).toLocaleString()}
              />
              <Metric
                label="Search docs deleted"
                value={(
                  opResult.searchIndexCleanup?.deletedSearchDocuments ?? 0
                ).toLocaleString()}
                tone={
                  opResult.searchIndexCleanup?.success === false
                    ? 'warning'
                    : undefined
                }
              />
            </StatGrid>
            {opResult.searchIndexCleanup?.success === false && (
              <Banner tone="warning">
                Search index cleanup skipped:{' '}
                {opResult.searchIndexCleanup.message ?? 'unknown reason'}
              </Banner>
            )}
            <RawJson data={opResult} />
          </>
        )}

        <ButtonRow>
          <DangerButton
            onClick={() => setDialog('duplicates')}
            disabled={running || groupCount === 0}
          >
            <Trash2 size={16} />
            Delete {recordsToDelete.toLocaleString()} duplicate
            {recordsToDelete === 1 ? '' : 's'}
          </DangerButton>
          {groupCount === 0 && !countLoading && (
            <span style={{ fontSize: 13, color: '#595959' }}>
              Nothing to delete.
            </span>
          )}
        </ButtonRow>
      </Card>

      <LastRunCard record={lastRunDupes} />

      {/* ---------------------- Cleanup stale events card ------------------- */}
      <Card>
        <CardTitle>Cleanup irrelevant events</CardTitle>
        <CardDescription>
          Drops events whose <code>StartDate</code> is more than{' '}
          <strong>1 year in the past</strong> or more than{' '}
          <strong>6 months in the future</strong>. Cascades to{' '}
          <code>EventInfoMap</code> and the Azure AI Search index.
        </CardDescription>
        <Banner tone="info">
          This is a separate operation from de-duplication. Run it occasionally
          to keep the index focused on currently relevant events.
        </Banner>

        {running && opKind === 'cleanup' && startedAt != null && (
          <RunningIndicator
            startedAt={startedAt}
            message="Removing stale events…"
          />
        )}

        {opKind === 'cleanup' && opError && (
          <Banner tone="error">{opError}</Banner>
        )}

        {opKind === 'cleanup' && opResult && !opError && (
          <>
            <Banner tone="success">
              {opResult.summary ?? 'Stale events removed successfully.'}
            </Banner>
            <StatGrid>
              <Metric
                label="EventInfo deleted"
                value={(opResult.deletedEventInfoRecords ?? 0).toLocaleString()}
                tone="warning"
              />
              <Metric
                label="EventInfoMap deleted"
                value={(opResult.deletedEventInfoMapRecords ?? 0).toLocaleString()}
              />
              <Metric
                label="Search docs deleted"
                value={(
                  opResult.searchIndexCleanup?.deletedSearchDocuments ?? 0
                ).toLocaleString()}
                tone={
                  opResult.searchIndexCleanup?.success === false
                    ? 'warning'
                    : undefined
                }
              />
            </StatGrid>
            {opResult.searchIndexCleanup?.success === false && (
              <Banner tone="warning">
                Search index cleanup skipped:{' '}
                {opResult.searchIndexCleanup.message ?? 'unknown reason'}
              </Banner>
            )}
            <RawJson data={opResult} />
          </>
        )}

        <ButtonRow>
          <DangerButton onClick={() => setDialog('cleanup')} disabled={running}>
            <CalendarX size={16} />
            Run cleanup
          </DangerButton>
        </ButtonRow>
      </Card>

      <LastRunCard record={lastRunCleanup} />

      {/* ---------------------------- Dialogs ------------------------------- */}
      <ConfirmDialog
        open={dialog === 'duplicates'}
        title="Delete duplicate records"
        body={
          <>
            <p>
              You're about to delete{' '}
              <strong>{recordsToDelete.toLocaleString()}</strong>{' '}
              <code>EventInfo</code> records across{' '}
              <strong>{groupCount.toLocaleString()}</strong> duplicate group
              {groupCount === 1 ? '' : 's'}. The oldest record in each group is
              preserved.
            </p>
            <p>
              Related <code>EventInfoMap</code> rows and Azure AI Search index
              documents will also be removed. This <strong>cannot be undone</strong>.
            </p>
          </>
        }
        promptText={<>Type <code>DELETE</code> to confirm:</>}
        confirmPhrase="DELETE"
        primaryLabel="Delete duplicates"
        onConfirm={doDeleteDuplicates}
        onCancel={() => setDialog(null)}
      />

      <ConfirmDialog
        open={dialog === 'cleanup'}
        title="Cleanup irrelevant events"
        body={
          <>
            <p>
              This will <strong>permanently delete</strong> all events whose{' '}
              <code>StartDate</code> is:
            </p>
            <ul>
              <li>more than <strong>1 year</strong> in the past, or</li>
              <li>more than <strong>6 months</strong> in the future.</li>
            </ul>
            <p>
              Related <code>EventInfoMap</code> rows and Azure AI Search index
              documents will be removed too. This cannot be undone.
            </p>
          </>
        }
        promptText={<>Type <code>CLEANUP</code> to confirm:</>}
        confirmPhrase="CLEANUP"
        primaryLabel="Run cleanup"
        onConfirm={doCleanupStale}
        onCancel={() => setDialog(null)}
      />

      <Card>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Copy size={18} />
          About this page
        </CardTitle>
        <CardDescription>
          Backed by <code>GET /api/eventInfoDuplicates?action=count|list</code>{' '}
          and <code>DELETE /api/eventInfoDuplicates?action=duplicates|cleanup&amp;confirm=true</code>.
        </CardDescription>
      </Card>
    </AdminLayout>
  );
};

export default AdminDuplicatesPage;
